import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { Modal } from "@/components/design/Modal";

// Intro phase of the LinkedIn import, split out of LinkedInImportDialog so the
// dialog itself only carries the return phase (poll → error → hand-off).
//
// The intro is gated on the member's region. LinkedIn only lets members from the
// European Economic Area consent to sharing their data with third-party apps:
// "Only LinkedIn users from the European Economic Area are allowed to consent to
// share their LinkedIn data with 3rd party developer applications."
// (learn.microsoft.com/en-us/linkedin/dma/member-data-portability/member-data-portability-3rd-party,
// scope r_dma_portability_3rd_party, checked 2026-09-10). The EEA is the EU plus
// Iceland, Liechtenstein and Norway; Switzerland and the UK are NOT included
// (Switzerland only counts for LinkedIn's self-serve *Member* product, which is
// not what we use). What matters is where the LinkedIn member is, not where our
// server runs, and there is no API to ask LinkedIn about it beforehand: sending a
// non-EEA member into the OAuth flow just produces an opaque failure at the end.
// So we ask up front, self-declared, like an age gate.
//
// Deliberately NOT persisted: the answer is a single click, it is cheap to repeat,
// and storing a self-declaration about a jurisdiction we never verify would only
// create a stale claim (people move, and a wrong stored "yes" would resurrect the
// opaque failure it exists to prevent).
type IntroStep = "region" | "outsideEea" | "connect";

export function LinkedInImportIntro({
  title,
  startHref,
  onClose,
}: {
  title: string;
  // Full-page navigation target that starts the BFF OAuth flow.
  startHref: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState<IntroStep>("region");

  if (step === "region") {
    return (
      <Modal
        open
        onClose={onClose}
        title={title}
        footer={
          <>
            <Button id="linkedin-import-region-no" variant="secondary" onClick={() => setStep("outsideEea")}>
              <FormattedMessage id="linkedin.import.regionNo" defaultMessage="No, outside the EEA" />
            </Button>
            <Button id="linkedin-import-region-yes" variant="primary" onClick={() => setStep("connect")}>
              <FormattedMessage id="linkedin.import.regionYes" defaultMessage="Yes, in the EEA" />
            </Button>
          </>
        }
      >
        <p id="linkedin-import-region-question" className="leading-snug text-ink">
          <FormattedMessage
            id="linkedin.import.regionQuestion"
            defaultMessage="Do you live in the European Economic Area?"
          />
        </p>
        <p className="smycles-field-meta mt-2 leading-snug text-ink">
          <FormattedMessage
            id="linkedin.import.regionExplain"
            defaultMessage="LinkedIn only hands profile data to other applications for members in the European Economic Area (the EU plus Iceland, Liechtenstein and Norway). This follows from the EU Digital Markets Act. Switzerland and the United Kingdom are not part of it."
          />
        </p>
      </Modal>
    );
  }

  if (step === "outsideEea") {
    return (
      <Modal
        open
        onClose={onClose}
        title={title}
        footer={
          <>
            {/* Back, not a dead end: a mis-click on "No" must not force the user to
                reopen the dialog from the service card. */}
            <Button id="linkedin-import-region-back" variant="secondary" onClick={() => setStep("region")}>
              <FormattedMessage id="common.back" defaultMessage="Back" />
            </Button>
            <Button id="linkedin-import-region-blocked-close" variant="primary" onClick={onClose}>
              <FormattedMessage id="linkedin.import.close" defaultMessage="Close" />
            </Button>
          </>
        }
      >
        {/* The question is gone from view at this point, so the step carries its
            own headline instead of a text that only reads as an answer. */}
        <p id="linkedin-import-region-blocked" className="leading-snug text-ink">
          <FormattedMessage
            id="linkedin.import.regionBlockedTitle"
            defaultMessage="The LinkedIn import only works inside the EEA"
          />
        </p>
        <p className="smycles-field-meta mt-2 leading-snug text-ink">
          <FormattedMessage
            id="linkedin.import.regionBlocked"
            defaultMessage="LinkedIn only allows this consent for members in the European Economic Area. You can fill your profile from a CV instead – upload it under Service and we will extract your positions and skills."
          />
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button id="linkedin-import-cancel" variant="secondary" onClick={onClose}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          {/* Real link (not a button + JS navigation) per the design guideline:
              this leaves the SPA for the BFF's OAuth start route. */}
          <a id="linkedin-import-connect" href={startHref} className={buttonClasses({ variant: "primary" })}>
            <FormattedMessage id="linkedin.import.connect" defaultMessage="Connect LinkedIn" />
          </a>
        </>
      }
    >
      <p className="smycles-field-meta leading-snug text-ink">
        <FormattedMessage
          id="linkedin.import.intro"
          defaultMessage="Connect your LinkedIn account to import your profile, positions and skills. You'll review what to keep or replace on your profile before anything is saved."
        />
      </p>
    </Modal>
  );
}
